import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/feature/Header';
import Footer from '../components/feature/Footer';
import { openConsentPreferences } from '@/utils/consent';

const EMPRESA = 'STIB SOLUÇÕES EM TRADUÇÃO E INTERPRETAÇÃO NO BRASIL LTDA';
const CNPJ = '62.613.986/0001-22';
const ENDERECO = 'Av. Brigadeiro Faria Lima, 1572, Sala 1022, São Paulo/SP, CEP 01451-917';
const EMAIL = 'traducao@stibtraducaosimultanea.com.br';
const ENCARREGADO = 'Daniel Batista de Carvalho Fernandes';

const linkClass = 'underline font-semibold text-primary-800 dark:text-primary hover:text-primary-900';

function H2({ n, children }: { n: number; children: string }) {
  return (
    <h2 className="mt-12 mb-4 font-heading text-2xl font-bold text-gray-900 dark:text-white scroll-mt-28" id={'secao-' + n}>
      {n}. {children}
    </h2>
  );
}

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-4 font-body text-base leading-relaxed text-gray-700 dark:text-gray-300">{children}</p>
);

const UL = ({ items }: { items: React.ReactNode[] }) => (
  <ul className="mb-4 list-disc space-y-2 pl-6 font-body text-base leading-relaxed text-gray-700 dark:text-gray-300">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

export default function PoliticaPrivacidadePage() {
  useEffect(() => {
    document.title = 'Política de Privacidade e LGPD | Palestra Silenciosa SP — STIB';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Saiba quais dados pessoais a Palestra Silenciosa SP (STIB) coleta, para que usa, com quem compartilha, por quanto tempo guarda e como exercer seus direitos pela LGPD.'
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://stibpalestrasilenciosasp.com.br/politica-privacidade');
    const robots = document.querySelector('meta[name="robots"]');
    const previousRobots = robots ? robots.getAttribute('content') : null;
    if (robots) robots.setAttribute('content', 'noindex, follow');
    window.scrollTo(0, 0);
    return () => {
      if (robots && previousRobots !== null) robots.setAttribute('content', previousRobots);
    };
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-white dark:bg-gray-900">
        <section className="bg-gradient-to-br from-primary/5 to-secondary/10 dark:from-gray-800 dark:to-gray-900 pt-36 pb-12">
          <div className="max-w-3xl mx-auto px-6">
            <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 font-body text-sm text-gray-600 dark:text-gray-300">
              <Link to="/" className="hover:underline">Início</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Política de Privacidade</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              Política de Privacidade e proteção de dados (LGPD)
            </h1>
            <p className="mt-4 font-body text-base text-gray-600 dark:text-gray-300">
              Versão 1.0 — vigente desde 10 de outubro de 2026. Escrevemos em linguagem simples para você saber,
              sem rodeios, o que fazemos com os seus dados pessoais.
            </p>
          </div>
        </section>

        <article className="max-w-3xl mx-auto px-6 pb-20">
          <H2 n={1}>Quem é o responsável pelos seus dados</H2>
          <P>
            A Palestra Silenciosa SP é uma marca operada por <strong>{EMPRESA}</strong>, CNPJ {CNPJ}, com sede em{' '}
            {ENDERECO}. Para a Lei Geral de Proteção de Dados (Lei 13.709/2018, a LGPD), somos a
            <strong> controladora</strong> dos dados tratados neste site (stibpalestrasilenciosasp.com.br).
          </P>
          <P>
            O <strong>encarregado pelo tratamento de dados pessoais</strong> (DPO) é {ENCARREGADO}. Contato:{' '}
            <a className={linkClass} href={'mailto:' + EMAIL}>{EMAIL}</a>.
          </P>

          <H2 n={2}>Quais dados coletamos</H2>
          <P>Coletamos somente o necessário e apenas nas situações abaixo.</P>
          <UL
            items={[
              <><strong>Dados que você informa nos formulários</strong> (orçamento, contato, guia, rodapé): nome, e-mail, telefone/WhatsApp, empresa, cidade e as informações do evento que você escolher descrever (data, local, número de participantes, idiomas, mensagem).</>,
              <><strong>Conversas por WhatsApp, telefone e e-mail</strong> quando você nos procura: o conteúdo da conversa e o número usado.</>,
              <><strong>Dados técnicos de navegação</strong>: endereço IP, tipo de navegador e dispositivo, páginas acessadas e data/hora. São registrados pelo servidor de hospedagem para segurança e funcionamento. A medição de audiência (Google Analytics) só ocorre se você permitir.</>,
              <><strong>Suas escolhas de privacidade e de tema</strong>, guardadas no seu próprio navegador (veja a <Link to="/politica-cookies" className={linkClass}>Política de Cookies</Link>).</>,
            ]}
          />
          <P>Não pedimos dados pessoais sensíveis (como saúde, religião ou biometria). Por favor, não os envie nas mensagens livres.</P>

          <H2 n={3}>Para que usamos e em que base legal</H2>
          <div className="mb-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-left font-body text-sm text-gray-700 dark:text-gray-300">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white">
                <tr>
                  <th className="px-4 py-3 font-semibold">Finalidade</th>
                  <th className="px-4 py-3 font-semibold">Base legal (LGPD, art. 7º)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr><td className="px-4 py-3">Responder a pedidos de contato e elaborar orçamentos</td><td className="px-4 py-3">Procedimentos preliminares relacionados a contrato a pedido do titular (inciso V)</td></tr>
                <tr><td className="px-4 py-3">Executar o serviço contratado (locação de equipamentos, equipe em campo) e emitir documentos fiscais</td><td className="px-4 py-3">Execução de contrato (inciso V) e cumprimento de obrigação legal ou regulatória (inciso II)</td></tr>
                <tr><td className="px-4 py-3">Segurança do site, prevenção a fraudes e a abusos</td><td className="px-4 py-3">Legítimo interesse (inciso IX)</td></tr>
                <tr><td className="px-4 py-3">Mapas do Google e medição de audiência</td><td className="px-4 py-3">Consentimento (inciso I), que você pode retirar a qualquer momento</td></tr>
                <tr><td className="px-4 py-3">Enviar o guia solicitado ou conteúdos por e-mail (inscrição)</td><td className="px-4 py-3">Consentimento (inciso I)</td></tr>
                <tr><td className="px-4 py-3">Exercício regular de direitos em processos judiciais, administrativos ou arbitrais</td><td className="px-4 py-3">Inciso VI</td></tr>
              </tbody>
            </table>
          </div>
          <P>Não vendemos seus dados e não os usamos para decisões automatizadas nem para publicidade comportamental.</P>

          <H2 n={4}>Com quem compartilhamos</H2>
          <P>Compartilhamos dados apenas com quem precisa deles para prestar o serviço, e somente o necessário:</P>
          <UL
            items={[
              <><strong>Hostinger</strong>: hospedagem do site e do sistema que recebe os pedidos de orçamento.</>,
              <><strong>Provedor de e-mail corporativo</strong>: envio e recebimento das mensagens ligadas ao seu pedido.</>,
              <><strong>Google</strong>: fontes de texto (Google Fonts) carregadas ao abrir o site; mapas e análise de audiência apenas com o seu consentimento.</>,
              <><strong>jsDelivr (CDN)</strong>: entrega dos ícones do site.</>,
              <><strong>WhatsApp (Meta)</strong>: quando você escolhe falar conosco por esse aplicativo, valem também os termos e a política deles.</>,
              <><strong>Intérpretes, técnicos e parceiros de execução</strong> do seu evento, apenas com as informações necessárias para atendê-lo.</>,
              <><strong>Contabilidade e autoridades</strong>, quando a lei exigir.</>,
            ]}
          />

          <H2 n={5}>Transferência internacional</H2>
          <P>
            Alguns fornecedores acima (como Google, Hostinger e Meta) podem tratar dados em servidores fora do Brasil.
            Quando isso ocorrer, buscamos fornecedores que adotem garantias compatíveis com a LGPD, como cláusulas
            contratuais e padrões de segurança reconhecidos (art. 33).
          </P>

          <H2 n={6}>Por quanto tempo guardamos</H2>
          <UL
            items={[
              'Pedidos de orçamento que não viraram contrato: até 24 meses após o último contato.',
              'Mensagens de contato em geral: até 12 meses.',
              'Contratos, notas e documentos fiscais: pelo prazo que a lei exigir (em regra, 5 anos).',
              'Inscrição para receber conteúdos: até você cancelar.',
              'Registros técnicos de acesso: pelo prazo definido em lei e pelo provedor de hospedagem.',
            ]}
          />
          <P>Depois disso, eliminamos ou anonimizamos os dados, salvo se a lei nos obrigar a guardá-los.</P>

          <H2 n={7}>Cookies e tecnologias semelhantes</H2>
          <P>
            Usamos o mínimo necessário e só ativamos o que não é essencial com a sua permissão. Veja o detalhe na{' '}
            <Link to="/politica-cookies" className={linkClass}>Política de Cookies</Link> ou{' '}
            <button type="button" onClick={openConsentPreferences} className={linkClass + ' cursor-pointer'}>
              abra as suas preferências agora
            </button>
            .
          </P>

          <H2 n={8}>Seus direitos como titular</H2>
          <P>Pela LGPD (art. 18), você pode pedir a qualquer momento:</P>
          <UL
            items={[
              'confirmação de que tratamos seus dados e acesso a eles;',
              'correção de dados incompletos, inexatos ou desatualizados;',
              'anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade com a lei;',
              'portabilidade dos dados a outro fornecedor, nos termos da regulamentação;',
              'eliminação dos dados tratados com base no seu consentimento;',
              'informação sobre com quem compartilhamos seus dados;',
              'informação sobre a possibilidade de não dar consentimento e sobre as consequências;',
              'revogação do consentimento, sem prejuízo do que já foi tratado antes;',
              'oposição a tratamentos feitos sem consentimento, quando houver descumprimento da lei.',
            ]}
          />

          <H2 n={9}>Como exercer seus direitos</H2>
          <P>
            Escreva para <a className={linkClass} href={'mailto:' + EMAIL}>{EMAIL}</a> informando seu nome e o que
            deseja. Podemos pedir uma confirmação de identidade para proteger você. Respondemos em até 15 dias. Se
            não ficar satisfeito, você pode reclamar à Autoridade Nacional de Proteção de Dados (ANPD) em{' '}
            <a className={linkClass} href="https://www.gov.br/anpd" target="_blank" rel="noopener noreferrer">gov.br/anpd</a>.
          </P>

          <H2 n={10}>Segurança</H2>
          <P>
            Usamos conexão criptografada (HTTPS), acesso restrito aos sistemas e fornecedores com padrões de
            segurança reconhecidos. Nenhum sistema é 100% imune, mas, se houver um incidente que possa causar risco
            relevante a você, avisaremos você e a ANPD conforme a LGPD (art. 48).
          </P>

          <H2 n={11}>Crianças e adolescentes</H2>
          <P>
            Nossos serviços são voltados a empresas e organizadores de eventos. Não coletamos intencionalmente dados
            de crianças e adolescentes; se tomarmos conhecimento, eliminaremos essas informações.
          </P>

          <H2 n={12}>Mudanças nesta política</H2>
          <P>
            Podemos atualizar este texto para refletir mudanças no site ou na lei. A versão e a data de vigência
            ficam no topo da página. Mudanças relevantes serão destacadas no site.
          </P>

          <div className="mt-12 rounded-2xl bg-gray-50 dark:bg-gray-800 p-6 font-body text-sm text-gray-700 dark:text-gray-300">
            <strong>Contato do encarregado (DPO):</strong> {ENCARREGADO} —{' '}
            <a className={linkClass} href={'mailto:' + EMAIL}>{EMAIL}</a>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
